import { useState, useEffect, useRef, createContext, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Plus, HelpCircle, X } from "lucide-react";
import { lbl, opts } from "./config";

export const inputCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
export const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

// single-open help coordination (one open help per form/section)
export const HelpCtx = createContext({ openKey: null, setOpenKey: () => {} });
export const useHelpForm = () => { const [openKey, setOpenKey] = useState(null); return { openKey, setOpenKey }; };

export const FieldHelp = ({ fkey, help, example }) => {
  const { openKey, setOpenKey } = useContext(HelpCtx);
  if (!help && !example) return null;
  const open = openKey === fkey;
  return (
    <>
      <button type="button" data-testid={`help-${fkey}`} onClick={() => setOpenKey(open ? null : fkey)}
        className="text-muted-foreground hover:text-foreground ml-1" title="Aiuto" aria-label="Aiuto">
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span className="block basis-full mt-1 text-xs text-muted-foreground bg-secondary/40 rounded-md p-2" data-testid={`help-body-${fkey}`}>
          {help}{example ? <><br /><i>Es. {example}</i></> : null}
        </span>
      )}
    </>
  );
};

export const AutoTextarea = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  const resize = () => { const el = ref.current; if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 320) + "px"; };
  useEffect(() => { resize(); }, [value]);
  return <textarea ref={ref} data-testid={testid} className={`${inputCls} resize-none overflow-hidden`} rows={2} value={value || ""}
    onChange={(e) => onChange(e.target.value)} onInput={resize} />;
};

const MultiField = ({ f, value, onChange, testid }) => {
  const sel = asArray(value);
  const toggle = (v) => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {opts(f.group).map((o) => (
        <button key={o.value} type="button" data-testid={`${testid}-opt-${o.value}`} onClick={() => toggle(o.value)}
          className={`text-xs rounded-full px-3 py-1 border transition-colors ${sel.includes(o.value) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input text-muted-foreground hover:border-primary"}`}>{o.label}</button>
      ))}
    </div>
  );
};

const LinkField = ({ f, value, onChange, options, testid }) => {
  const sel = asArray(value);
  const list = options?.[f.linkType] || [];
  const toggle = (id) => onChange(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  if (list.length === 0) return <p className="text-xs text-muted-foreground">Nessuna voce disponibile per questo cane.</p>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {list.map((o) => (
        <button key={o.id} type="button" onClick={() => toggle(o.id)}
          className={`text-xs rounded-full px-3 py-1 border transition-colors max-w-full truncate ${sel.includes(o.id) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input text-muted-foreground hover:border-primary"}`}>{o.title}</button>
      ))}
    </div>
  );
};

// Non-recursive editor for repeatable sub-fields (text / enum / area only).
const SubFieldEdit = ({ f, value, onChange, testid }) => {
  if (f.type === "enum") return (
    <select data-testid={testid} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled={!!f.required}>Seleziona…</option>
      {opts(f.group).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  if (f.type === "area") return <AutoTextarea value={value} onChange={onChange} testid={testid} />;
  return <input data-testid={testid} className={inputCls} value={value || ""} placeholder={f.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
};

const RepeatableField = ({ f, value, onChange, testid }) => {
  const rows = asArray(value);
  const add = () => onChange([...rows, {}]);
  const upd = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="rounded-lg border border-border p-2 space-y-2 bg-secondary/20" data-testid={`${testid}-row-${i}`}>
          <div className="flex justify-end"><button type="button" onClick={() => del(i)} className="text-muted-foreground hover:text-destructive" data-testid={`${testid}-del-${i}`}><X className="h-4 w-4" /></button></div>
          {f.item.map((sf) => (
            <div key={sf.key}>
              <div className="text-xs text-muted-foreground">{sf.label}</div>
              <div className="mt-0.5"><SubFieldEdit f={sf} value={r[sf.key]} onChange={(v) => upd(i, sf.key, v)} testid={`${testid}-${i}-${sf.key}`} /></div>
            </div>
          ))}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="rounded-full text-primary border-primary/40 hover:bg-primary/5" onClick={add} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi</Button>
    </div>
  );
};

export const FieldEdit = ({ f, value, onChange, options, testid }) => {
  const tid = testid || `fld-${f.key}`;
  if (f.type === "enum") return (
    <select data-testid={tid} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled={!!f.required}>Seleziona…</option>
      {opts(f.group).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  if (f.type === "multi") return <MultiField f={f} value={value} onChange={onChange} testid={tid} />;
  if (f.type === "link") return <LinkField f={f} value={value} onChange={onChange} options={options} testid={tid} />;
  if (f.type === "rep") return <RepeatableField f={f} value={value} onChange={onChange} testid={tid} />;
  if (f.type === "bool") return (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <input data-testid={tid} type="checkbox" className="h-4 w-4 cursor-pointer" checked={!!value} onChange={(e) => onChange(e.target.checked)} /> Sì
    </label>
  );
  if (f.type === "area") return <AutoTextarea value={value} onChange={onChange} testid={tid} />;
  return <input data-testid={tid} className={inputCls} value={value || ""} placeholder={f.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
};

export const FieldRow = ({ f, value, onChange, options }) => (
  <div>
    <div className="text-sm font-medium flex items-center flex-wrap">{f.label}{f.required ? <span className="text-destructive ml-0.5">*</span> : null}<FieldHelp fkey={f.key} help={f.help} example={f.example} /></div>
    <div className="mt-1"><FieldEdit f={f} value={value} onChange={onChange} options={options} /></div>
  </div>
);

export const readValue = (f, v, options) => {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  if (f.type === "bool") return v ? "Sì" : "No";
  if (f.type === "enum") return lbl(f.group, v);
  if (f.type === "multi") return asArray(v).map((k) => lbl(f.group, k)).join(", ");
  if (f.type === "link") {
    const list = options?.[f.linkType] || [];
    const names = asArray(v).map((id) => (list.find((o) => o.id === id)?.title) || null).filter(Boolean);
    return names.length ? names.join(", ") : `${asArray(v).length} voce/i`;
  }
  if (f.type === "rep") {
    const rows = asArray(v);
    if (!rows.length) return null;
    return (
      <ul className="list-disc pl-4 space-y-0.5">
        {rows.map((r, i) => {
          const parts = f.item.map((sf) => {
            const rv = r[sf.key]; if (rv === undefined || rv === null || rv === "") return null;
            return sf.type === "enum" ? lbl(sf.group, rv) : String(rv);
          }).filter(Boolean);
          return <li key={i}>{parts.join(" — ")}</li>;
        })}
      </ul>
    );
  }
  return <span className="whitespace-pre-line break-words">{String(v)}</span>;
};

export const hasVal = (f, v) => {
  if (v === undefined || v === null || v === false || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  return String(v).trim() !== "";
};

export function useKeyboardInset() {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onChange = () => setInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    onChange();
    return () => { vv.removeEventListener("resize", onChange); vv.removeEventListener("scroll", onChange); };
  }, []);
  return inset;
}

// Keyboard-aware action bar: when the mobile soft keyboard is open (visualViewport
// shrinks), pin the actions just above the keyboard as a fixed bar so Save/Cancel stay
// reachable. On desktop the keyboard inset is always 0, so behaviour is unchanged (sticky).
export const StickyActions = ({ children }) => {
  const inset = useKeyboardInset();
  const kbOpen = inset > 120;
  if (kbOpen) return (
    <div data-testid="sticky-actions" className="fixed left-0 right-0 z-[60] px-4 pt-2 pb-2 bg-background border-t border-border flex gap-2 justify-end shadow-[0_-6px_16px_rgba(0,0,0,0.12)]" style={{ bottom: inset }}>
      {children}
    </div>
  );
  return (
    <div data-testid="sticky-actions" className="sticky bottom-0 -mx-4 mt-2 px-4 py-3 bg-background/95 backdrop-blur border-t border-border flex gap-2 justify-end z-10" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
      {children}
    </div>
  );
};
